import { useEffect, useState } from "react";
import type { Peer } from "@circle-react/types/Live/Peer";

const DEFAULT_PAGE_SIZE = 6;

export const usePaginatedPeers = (
  peers: Peer[],
  startingPage = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) => {
  const [page, setPage] = useState(startingPage);
  const startingPeerIndex = (page - 1) * pageSize;
  const currentPagePeers = peers?.slice(
    startingPeerIndex,
    startingPeerIndex + pageSize,
  );
  const totalPages = Math.ceil(peers?.length / pageSize);
  const next = () => setPage(Math.min(page + 1, totalPages));
  const prev = () => setPage(Math.max(page - 1, 1));
  const goToPage = (pageNumber: number) =>
    setPage(Math.max(1, Math.min(pageNumber, totalPages)));
  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;

  // when totalPages changes, we need to navigate to previous page if it's out of bounds
  useEffect(() => {
    if (page > totalPages) {
      setPage(Math.max(totalPages, 1));
    }
  }, [page, totalPages]);

  return {
    currentPagePeers,
    page,
    totalPages,
    next,
    prev,
    goToPage,
    isFirstPage,
    isLastPage,
  };
};
